/**
 * `@lorcana/engine` — the rules core (SPEC.md §2, §4). Zero runtime dependencies.
 *
 * The public API is `createGame`, `legalActions`, `apply` (+ emitted events), `clone`,
 * `playerView`, `determinize`, `isTerminal`/`result`.
 *
 * ```ts
 * import { CARDS_BY_ID } from '@lorcana/card-data';
 *
 * const state = createGame({ decks: [deckA, deckB], seed: 7 }, CARDS_BY_ID);
 * while (!isTerminal(state)) {
 *   const [first] = legalActions(state);          // one pending ChoiceRequest, always
 *   const events = apply(state, first.key);        // mutates in place, returns events
 * }
 * result(state); // { kind: 'win', winner: 0, reason: 'lore' }
 * ```
 *
 * Coverage today: the full turn structure, inking, playing cards, questing, challenging, the
 * 20-lore win and the CR 2.2 deck-out rule (ticket 04) · the hidden-information layer
 * (ticket 05: `knownTo`, `playerView`, `determinize`) · the bag and the game state check
 * cascade (ticket 07) · and the ability layer (ticket 08): the combinator DSL in
 * `abilities/dsl.ts`, its serializable IR in `abilities/ir.ts`, the interpreter that runs
 * triggered, activated, static and action abilities through the resolution stack, and all 15
 * keywords · the cost system (ticket 09): `costs.ts` (total cost, payment modifiers, the
 * alternate-cost values) and `payments.ts` (every legal way to pay, enumerated as keyed `play`
 * options) · the continuous-effect model (ticket 10): `modifiers.ts` holds the modifier
 * records and the compute-on-read characteristic pipeline of SPEC.md §4.5 · the
 * damage/replacement pipeline (ticket 11): the four damage verbs in `damage.ts` and the
 * replacement interceptors in `replacements.ts`, which rewrite an event before it happens ·
 * the `custom()` escape hatch with its replay-to-resume machinery (ticket 13:
 * `abilities/custom.ts`) · and card stacks (ticket 12): `stacks.ts` holds the Shift and Boost
 * piles, whose top card is in play and whose under-cards are not. All 15 keywords are played
 * completely.
 */

export type { CardCatalog, CardDefinition, CardTypeName } from './cards.ts';
export {
  cardNamesOf,
  declarableNames,
  isSupportedCard,
  isVanillaCharacter,
  makeCatalog,
  requireDefinition,
} from './cards.ts';

export type { Action, ActionKind, ChoiceKind, ChoiceOption, ChoiceRequest } from './choices.ts';
export { actionKey } from './choices.ts';

export type { GameEvent, GameEventType } from './events.ts';

export type {
  AbilityInstance,
  BagItem,
  BagState,
  TriggerSource,
  TriggerSpec,
} from './bag.ts';
export {
  bagItemsFor,
  expireTriggers,
  nextBagHolder,
  registerTrigger,
  unregisterTrigger,
  unregisterTriggersFrom,
} from './bag.ts';

// -- the ability DSL and its IR (SPEC.md §5.1) -------------------------------

/**
 * The combinator builders. Card modules normally import them re-exported flat from
 * `@lorcana/cards`; here they are namespaced so the engine's own vocabulary stays clean.
 */
export * as dsl from './abilities/dsl.ts';
export { AbilityDefinitionError, defineAbilities } from './abilities/dsl.ts';

export type {
  Ability,
  AbilityKind,
  AbilityRegistry,
  ActionAbility,
  ActivatedAbility,
  ActivationCost,
  Amount,
  CardRef,
  CardTarget,
  ChallengeRole,
  Condition,
  CostApplies,
  DamageVerb,
  Duration,
  Effect,
  EffectStep,
  EventAmount,
  EventTemplate,
  InkColor,
  KeywordAbility,
  KeywordName,
  PlayerPool,
  PlayerRef,
  PlayerTarget,
  PlayerTargetSpec,
  ReplaceableEventKind,
  ReplacementAbility,
  Permission,
  PlayerPermission,
  ReplacementCondition,
  Restriction,
  Selector,
  Stat,
  StaticAbility,
  StaticEffect,
  TargetSpec,
  TriggerCondition,
  TriggeredAbility,
} from './abilities/ir.ts';
export {
  DAMAGE_VERBS,
  INK_COLORS,
  KEYWORD_NAMES,
  NUMERIC_KEYWORDS,
  PERMISSIONS,
  PLAYER_PERMISSIONS,
  RESTRICTIONS,
  STACKING_KEYWORDS,
  cloneIr,
} from './abilities/ir.ts';

// -- damage and the replacement pipeline (SPEC.md §4.1) ----------------------

export {
  banishEvent,
  dealDamage,
  discardPrevented,
  gainLoreEvent,
  inkEvent,
  loseLore,
  moveDamage,
  putDamage,
  removeDamage,
} from './damage.ts';

export type {
  BanishPendingEvent,
  DamagePendingEvent,
  DiscardPendingEvent,
  GainLorePendingEvent,
  InkPendingEvent,
  LoseLorePendingEvent,
  PendingEvent,
  ReplacementMatch,
  ToInkwellPendingEvent,
} from './replacements.ts';
export {
  consumeIfOneShot,
  nextReplacement,
  replacementsOf,
  rewriteEvent,
} from './replacements.ts';

export type { KeywordInstance } from './abilities/keywords.ts';
export {
  COMPLETE_KEYWORDS,
  PARTIAL_KEYWORDS,
  boostCostOf,
  canBeChosenBy,
  challengerBonusOf,
  hasKeyword,
  instanceHasKeyword,
  isKeywordOnlyAndComplete,
  keywordValue,
  keywordsOf,
  normaliseKeyword,
  printedKeywords,
  resistOf,
  shiftCostOf,
  singTogetherCostOf,
  singerValueOf,
} from './abilities/keywords.ts';

export type { AbilityContext } from './abilities/selectors.ts';
export {
  baseContext,
  evalAmount,
  evalCondition,
  matchesSelector,
  selectorPool,
  targetPool,
} from './abilities/selectors.ts';

// -- the `custom()` escape hatch (SPEC.md §5.2, §4.3) ------------------------

export type {
  CustomAbility,
  CustomChoiceSpec,
  CustomContext,
  CustomFact,
  CustomGenerator,
  CustomOptionSpec,
  CustomRead,
  CustomReply,
  CustomRun,
  CustomStep,
  CustomTranscriptStep,
  CustomYield,
} from './abilities/custom.ts';
export {
  CustomAbilityError,
  DECLINE_TOKEN,
  cardToken,
  chooseCard,
  chooseCardName,
  chooseOption,
  clearCustomRegistry,
  customAbilityIds,
  customIdsIn,
  denseOptions,
  hasCustom,
  missingCustomAbilities,
  nameToken,
  readAmount,
  readCount,
  readNames,
  readPool,
  readTest,
  registerCustom,
  requireCustom,
  runEffect,
  setCustom,
  setCustomHotReload,
  tokenCardName,
  tokenInstance,
  unregisterCustom,
  usesEscapeHatch,
} from './abilities/custom.ts';

export type { Activation, EffectFrame } from './abilities/interpret.ts';
export {
  abilitiesOf,
  activatedAbilityAt,
  canActivate,
  legalActivations,
  registerAbilitiesFor,
} from './abilities/interpret.ts';

export { cardSnapshot, irLine, irSnapshot, roundTrips } from './abilities/snapshot.ts';

export { bagWindowOpen, settle } from './gsc.ts';

export type { InstanceId, PlayerIndex, Zone } from './ids.ts';
export { PUBLIC_ZONES } from './ids.ts';

export type {
  AwaitedChoice,
  CardInstance,
  CardStack,
  Frame,
  FrameKind,
  GameResult,
  GameState,
  Modifier,
  ModifierScope,
  PlayerState,
  RuleOptions,
  TargetBinding,
  TurnPhase,
  WinReason,
} from './state.ts';
export {
  DEFAULT_RULE_OPTIONS,
  allPlayersMask,
  instanceAt,
  nextPlayer,
  playerAt,
  playerBit,
  turnOrderFrom,
  zoneOf,
} from './state.ts';

export type { RngState } from './rng.ts';
export { cloneRngState, createRngState, nextIntBelow, nextUint32, shuffleInPlace } from './rng.ts';

export {
  EngineError,
  IllegalActionError,
  advance,
  apply,
  isTerminal,
  legalActionKeys,
  legalActions,
  result,
} from './engine.ts';

export type { GameSetup, UnsupportedCard } from './setup.ts';
export { UnsupportedCardsError, createGame } from './setup.ts';

export { clone } from './clone.ts';

export type { PlayerView, PlayerZonesView, ViewBagItem, ViewEvent, ViewInstance } from './view.ts';
export {
  isBeingLookedAt,
  isRevealedDeckTop,
  isVisibleTo,
  knowsCard,
  playerView,
} from './view.ts';

export { determinize } from './determinize.ts';

export type { ActionLog } from './log.ts';
export { recordChoice, replay, replayStates, startLog } from './log.ts';

export {
  costOf,
  damageDealtBy,
  canChallenge,
  canExertForAction,
  canQuest,
  canReady,
  definitionOf,
  effectiveLoreOf,
  effectiveStrengthOf,
  isChallenging,
  loreOf,
  strengthOf,
  willpowerOf,
} from './characteristics.ts';

// -- continuous effects (SPEC.md §4.5) ---------------------------------------

export type {
  DurationBoundary,
  ModifierEffect,
  PlayerPermissionEffect,
  ReadyCapEffect,
} from './modifiers.ts';
export {
  addModifier,
  applyModifierEffect,
  applyPlayerPermissionEffect,
  applyReadyCapEffect,
  canGainLore,
  challengeDamageStatOf,
  cloneModifier,
  drawStepSkipped,
  dropModifiersFor,
  expireModifiers,
  grantedAbilities,
  grantedKeywords,
  handCountsAsInkable,
  inkLimitOf,
  isAllowed,
  isRestricted,
  loreToWinOf,
  lostKeywords,
  playerPermissionCount,
  readyCapOf,
  retargetModifiers,
  statBonus,
} from './modifiers.ts';

export type { MoveCardOptions, ZonePlacement } from './zones.ts';
export {
  availableInk,
  cardsIn,
  charactersAt,
  charactersInPlay,
  discardCard,
  lookAtCard,
  lookAtDeck,
  moveCardTo,
  moveToLocation,
  putIntoHand,
  putIntoInkwell,
  putOnDeck,
  revealTo,
  stopLooking,
} from './zones.ts';

// -- card stacks: Shift and Boost piles (SPEC.md §4.1) -----------------------

export {
  boostUnder,
  cardsUnder,
  dissolveStack,
  extractFromStack,
  isUnderCard,
  leaveUnderPile,
  putCardUnder,
  returnTemporaryShifts,
  shiftOnto,
  stackOf,
  stackToppedBy,
  topCardOf,
  underCount,
} from './stacks.ts';

// -- the cost system (SPEC.md §4.1) ------------------------------------------

export type { CostModifier, PaymentPlan, ShiftKind, ShiftSpec } from './costs.ts';
export {
  INK_PAYMENT,
  abilityInkCostOf,
  addCostModifier,
  canPlayForFree,
  consumeOneShotModifiers,
  expireCostModifiers,
  isSong,
  matchesCostFilter,
  oneShotModifiersFor,
  parseShiftVariant,
  paymentAdjustment,
  shiftBaseSets,
  shiftSpecOf,
  shiftTotalCostOf,
  singCostOf,
  singTogetherRequirementOf,
  singerContributionOf,
  turnActionInkCostOf,
} from './costs.ts';

export type { PaymentResult } from './payments.ts';
export {
  SHIFT_PLAYS_ENABLED,
  canPayFor,
  legalPlayOptions,
  payFor,
  paymentPlansFor,
} from './payments.ts';

export type { GameStateCheckOptions } from './rules.ts';
export {
  hasCompulsoryChallenge,
  legalChallengeTargets,
  legalMoveOptions,
  legalTurnActions,
  moveCharacter,
  moveCostOf,
  runGameStateChecks,
} from './rules.ts';

export type { RandomGameOptions, RandomGameResult } from './random-bot.ts';
export { chooseRandomLegalKey, playRandomGame } from './random-bot.ts';

/** Shape every workspace package reports itself with. */
export interface PackageStatus {
  /** npm name of the workspace package. */
  readonly name: string;
  /** Which spec section defines this package's job. */
  readonly specSection: string;
  /** `stub` until the package's implementing ticket lands, then `implemented`. */
  readonly state: 'implemented' | 'stub';
  /** Workspace packages this one is built on. */
  readonly dependsOn: readonly string[];
  /** One line of substance for implemented packages, e.g. how much data they carry. */
  readonly detail?: string;
}

export const ENGINE_PACKAGE = '@lorcana/engine';

export function engineStatus(): PackageStatus {
  return {
    name: ENGINE_PACKAGE,
    specSection: 'SPEC.md §4 — rules engine',
    state: 'implemented',
    dependsOn: [],
    detail:
      'turn structure, ink, play, quest, challenge, lore/deck-out, bag + GSC, ability DSL/IR ' +
      'interpreter, all 15 keywords, and the cost system (alternate costs + payment modifiers)',
  };
}
